import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout } from "./components/layout";
import { useExam } from "@/context/exam-context";
import HomePage from "./pages";
import LoginPage from "./pages/login";
import RegisterPage from "./pages/register/index";
import DashboardPage from "./pages/dashboard";
import ExamsPage from "./pages/exams";
import ExamDetailsPage from "./pages/exams/[id]";
import ExamAttemptPage from "./pages/exams/[id]/attempt";
import ResultsPage from "./pages/results";
import ExamResultPage from "./pages/results/[id]";
import RankingsPage from "./pages/rankings";
import ProfilePage from "./pages/profile";
import SettingsPage from "./pages/settings";
import OfflinePage from "./pages/offline";
import AdminLayout from "./pages/admin/layout";
import AdminPage from "./pages/admin";
import AdminQuestionsPage from "./pages/admin/questions";
import AdminUsersPage from "./pages/admin/users";
import AdminExamsPage from "./pages/admin/exams";
import AdminRegistrationsPage from "./pages/admin/registrations";
import { useEffect } from "react";

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, authInitialized } = useExam();
  const location = useLocation();
  
  // Show nothing while checking auth
  if (!authInitialized || loading) {
    return null;
  }
  
  // Redirect to login if not authenticated
  if (!user) {
    // Check if we're trying to access an exam page
    const match = location.pathname.match(/^\/exams\/([a-zA-Z0-9]+)$/);
    if (match && match[1]) {
      // Store the exam ID for redirect after login
      localStorage.setItem('shared_exam_id', match[1]);
    }
    
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Admin Route component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, authInitialized } = useExam();
  
  // Show nothing while checking auth
  if (!authInitialized || loading) {
    return null;
  }
  
  // Redirect to dashboard if not admin
  if (!user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

// Exam Route Guard to prevent navigation while taking exam
function ExamRouteGuard({ children }: { children: React.ReactNode }) {
  const { isAttemptingExam, currentAttempt } = useExam();
  const location = useLocation();
  
  // Only allow exam attempt page when actively taking an exam
  if (isAttemptingExam && currentAttempt && !location.pathname.includes(`/exams/${currentAttempt.examId}/attempt`)) {
    return <Navigate to={`/exams/${currentAttempt.examId}/attempt`} replace />;
  }
  
  return <>{children}</>;
}

export default function App() {
  const { isAttemptingExam } = useExam();
  
  // Prevent browser back button while taking exam
  useEffect(() => {
    if (isAttemptingExam) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "You have an exam in progress. Are you sure you want to leave?";
        return e.returnValue;
      };
      
      const handlePopState = (e: PopStateEvent) => {
        if (isAttemptingExam) {
          window.history.pushState(null, '', window.location.pathname);
        }
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      
      // Push state to replace current history entry so back button doesn't exit immediately
      window.history.pushState(null, '', window.location.pathname);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isAttemptingExam]);
  
  return (
    <>
      <Layout>
        <ExamRouteGuard>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/offline" element={<OfflinePage />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/exams" element={<ProtectedRoute><ExamsPage /></ProtectedRoute>} />
            <Route path="/exams/:id" element={<ProtectedRoute><ExamDetailsPage /></ProtectedRoute>} />
            <Route path="/exams/:id/attempt" element={<ProtectedRoute><ExamAttemptPage /></ProtectedRoute>} />
            <Route path="/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
            <Route path="/results/:id" element={<ProtectedRoute><ExamResultPage /></ProtectedRoute>} />
            <Route path="/rankings" element={<ProtectedRoute><RankingsPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminRoute><AdminLayout><AdminPage /></AdminLayout></AdminRoute>} />
            <Route path="/admin/questions" element={<AdminRoute><AdminLayout><AdminQuestionsPage /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminLayout><AdminUsersPage /></AdminLayout></AdminRoute>} />
            <Route path="/admin/exams" element={<AdminRoute><AdminLayout><AdminExamsPage /></AdminLayout></AdminRoute>} />
            <Route path="/admin/registrations" element={<AdminRoute><AdminLayout><AdminRegistrationsPage /></AdminLayout></AdminRoute>} />
          </Routes>
        </ExamRouteGuard>
      </Layout>
    </>
  );
}